/**
 * Vimsottari Dasha Data Types and Constants
 * 120-year planetary period system (Bhrigu Parampara)
 */

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Bhrigu Parampara sequence (Surya-first)
 */
export const DASA_SEQUENCE = [
  'Surya', 'Chandra', 'Mangala', 'Rahu', 'Guru',
  'Shani', 'Budha', 'Ketu', 'Shukra'
] as const;

export type DasaPlanet = typeof DASA_SEQUENCE[number];

/**
 * Planet period durations in years (total: 120)
 */
export const DASA_PERIODS: Record<DasaPlanet, number> = {
  Surya: 6,
  Chandra: 10,
  Mangala: 7,
  Rahu: 18,
  Guru: 16,
  Shani: 19,
  Budha: 17,
  Ketu: 7,
  Shukra: 20,
};

/**
 * Planet short codes
 */
export const PLANET_CODES: Record<DasaPlanet, string> = {
  Surya: 'Sy',
  Chandra: 'Ch',
  Mangala: 'Ma',
  Rahu: 'Ra',
  Guru: 'Gu',
  Shani: 'Sa',
  Budha: 'Bu',
  Ketu: 'Ke',
  Shukra: 'Sk',
};

/**
 * Level names (1-6)
 */
export const DASHA_LEVEL_NAMES = [
  'Mahadasha',
  'Antardasha',
  'Pratyantardasha',
  'Sookshma',
  'Prana',
  'Deha'
] as const;

export type DashaLevelName = typeof DASHA_LEVEL_NAMES[number];

/**
 * Level abbreviations for display
 */
export const DASHA_LEVEL_ABBREV: Record<DashaLevelName, string> = {
  Mahadasha: 'MD',
  Antardasha: 'AD',
  Pratyantardasha: 'PD',
  Sookshma: 'SD',
  Prana: 'PAD',
  Deha: 'DD',
};

/**
 * Maximum nesting depth
 */
export const MAX_DASHA_LEVEL = 6;

// ============================================================================
// STARTING POINT & CALCULATION METHOD
// ============================================================================

/**
 * Starting point options for Dasha calculation
 */
export const DASHA_STARTING_POINTS = [
  'Chandra', 'Lagna', 'Surya', 'Mangala', 'Budha', 'Guru', 'Shukra', 'Shani', 'Rahu', 'Ketu', 'Custom'
] as const;

export type DashaStartingPoint = typeof DASHA_STARTING_POINTS[number];

export const DASHA_STARTING_POINT_INFO: Record<DashaStartingPoint, { label: string; shortCode: string }> = {
  Chandra: { label: 'Chandra', shortCode: 'Ch' },
  Lagna: { label: 'Lagna', shortCode: 'Lg' },
  Surya: { label: 'Surya', shortCode: 'Sy' },
  Mangala: { label: 'Mangala', shortCode: 'Ma' },
  Budha: { label: 'Budha', shortCode: 'Bu' },
  Guru: { label: 'Guru', shortCode: 'Gu' },
  Shukra: { label: 'Shukra', shortCode: 'Sk' },
  Shani: { label: 'Shani', shortCode: 'Sa' },
  Rahu: { label: 'Rahu', shortCode: 'Ra' },
  Ketu: { label: 'Ketu', shortCode: 'Ke' },
  Custom: { label: 'Custom', shortCode: 'Cu' },
};

/**
 * Custom starting point settings
 */
export interface DashaCustomStartPoint {
  degrees: number;
  label: string;
}

/**
 * Year type for calculation method
 */
export const DASHA_YEAR_TYPES = [
  'actual_lunar',
  'mean_tropical',
  'true_tropical',
  'mean_sidereal',
  'true_sidereal',
  'savana',
  'tithi_360',
  'user_defined'
] as const;

export type DashaYearType = typeof DASHA_YEAR_TYPES[number];

export interface DashaYearTypeInfo {
  label: string;
  days: string;
  description: string;
}

export const DASHA_YEAR_TYPE_INFO: Record<DashaYearType, DashaYearTypeInfo> = {
  actual_lunar: {
    label: 'Actual Lunar Year',
    days: '354-384',
    description: 'Lunar year from Chaitra Shukla Pratipada. Accounts for Adhika Masa.'
  },
  mean_tropical: {
    label: 'Mean Tropical',
    days: '365.24219',
    description: 'Average time between vernal equinoxes.'
  },
  true_tropical: {
    label: 'True Tropical',
    days: '~365.24',
    description: 'Exact equinox-to-equinox calculation for each year.'
  },
  mean_sidereal: {
    label: 'Mean Sidereal',
    days: '365.256363',
    description: 'Average time for Sun to return to same position relative to fixed stars.'
  },
  true_sidereal: {
    label: 'True Sidereal',
    days: '~365.26',
    description: 'Sun returns to same sidereal longitude (Rohini Paksha ayanamsa).'
  },
  savana: {
    label: 'Savana',
    days: '360',
    description: 'Traditional Vedic sacrificial year (360 sunrise-to-sunrise days).'
  },
  tithi_360: {
    label: 'Tithi 360',
    days: '354.37',
    description: '360 tithis (12 months × 30 tithis).'
  },
  user_defined: {
    label: 'Custom',
    days: 'N',
    description: 'User-defined year length in days.'
  },
};

export const DEFAULT_STARTING_POINT: DashaStartingPoint = 'Chandra';
export const DEFAULT_YEAR_TYPE: DashaYearType = 'actual_lunar';

// ============================================================================
// API TYPES
// ============================================================================

/**
 * API Request
 */
export interface VimsottariDasaRequest {
  datetime: string;
  latitude: number;
  longitude: number;
  reference_longitude: number;
  reference_name: string;
  timezone_offset: number;
  place_name?: string;
  altitude?: number;
  year_type?: DashaYearType;
  user_year_days?: number;
}

/**
 * API Response
 */
export interface VimsottariDasaResponse {
  birth_info: {
    datetime: string;
    location: string;
    place_name: string | null;
    reference_name: string;
    reference_longitude: number;
    birth_nakshatra: string;
    birth_nakshatra_pada: number;
    nakshatra_completion: number;
    birth_dasa_lord: string;
    year_type: string;
    year_days: number;
  };
  years_data: {
    start_year: number;
    start_date: string;
    timezone_offset: number;
    durations: number[];
  };
  mahadasa_periods: MahadasaPeriod[];
  constants: {
    dasa_sequence: string[];
    dasa_periods: Record<string, number>;
  };
  calculation_time_ms: number;
}

/**
 * Mahadasa period from API
 */
export interface MahadasaPeriod {
  planet: string;
  planet_code: string;
  start_date: string;
  end_date: string;
  total_years: number;
  actual_days: number;
}

// ============================================================================
// FRONTEND TYPES
// ============================================================================

/**
 * Generic dasha period (any level)
 */
export interface DashaPeriod {
  /** Planet chain from Mahadasha to current level */
  planets: string[];
  /** Start date */
  startDate: Date;
  /** End date */
  endDate: Date;
  /** Duration in days */
  durationDays: number;
  /** Level (1-6) */
  level: number;
  /** Is this the current active period? */
  isCurrent?: boolean;
}

/**
 * UI state for drill-down accordion
 */
export interface VimsottariUIState {
  /** Expanded path: planet names at each level */
  expandedPath: string[];
  /** Calculated periods cache */
  cache: DashaCache;
}

/**
 * Cache for calculated sub-periods
 */
export interface DashaCache {
  /** Key format: "planet1-planet2-planet3" */
  [key: string]: DashaPeriod[];
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get planet sequence starting from given planet
 */
export function getSequenceFrom(startPlanet: string): string[] {
  const startIndex = DASA_SEQUENCE.indexOf(startPlanet as DasaPlanet);
  if (startIndex === -1) return [...DASA_SEQUENCE];

  const result: string[] = [];
  for (let i = 0; i < 9; i++) {
    result.push(DASA_SEQUENCE[(startIndex + i) % 9]);
  }
  return result;
}

/**
 * Get level name by number (1-6)
 */
export function getLevelName(level: number): DashaLevelName {
  return DASHA_LEVEL_NAMES[level - 1] || DASHA_LEVEL_NAMES[0];
}

/**
 * Get level abbreviation
 */
export function getLevelAbbrev(level: number): string {
  const name = getLevelName(level);
  return DASHA_LEVEL_ABBREV[name];
}

/**
 * Format period name: "Shani-Budha-Ketu"
 */
export function formatPeriodName(planets: string[]): string {
  return planets.join('-');
}

/**
 * Get cache key for period
 */
export function getCacheKey(planets: string[]): string {
  return planets.join('-');
}

/**
 * Check if date is within period
 */
export function isDateInPeriod(date: Date, period: DashaPeriod): boolean {
  return date >= period.startDate && date < period.endDate;
}

/**
 * Get planet code
 */
export function getPlanetCode(planet: string): string {
  return PLANET_CODES[planet as DasaPlanet] || planet.substring(0, 2);
}

/**
 * Get planet years
 */
export function getPlanetYears(planet: string): number {
  return DASA_PERIODS[planet as DasaPlanet] || 0;
}
